import { PiPauseFill } from '@react-icons/all-files/pi/PiPauseFill'
import { PiPlayFill } from '@react-icons/all-files/pi/PiPlayFill'
import { PiSkipBackBold } from '@react-icons/all-files/pi/PiSkipBackBold'
import { PiSkipForwardBold } from '@react-icons/all-files/pi/PiSkipForwardBold'
import React, { useContext } from 'react'

import { ThemeContext } from 'contexts/theme'

import { AccessibleIcon, IconButton } from 'components/ds'

type ButtonTypes = 'play' | 'pause' | 'skip-back' | 'skip-forward'
type Props = {
  onClick: (...args: Array<any>) => any
  type?: ButtonTypes
  size?: React.ComponentProps<typeof IconButton>['size']
}
const iconMap: Record<ButtonTypes, any> = {
  play: PiPlayFill,
  pause: PiPauseFill,
  'skip-back': PiSkipBackBold,
  'skip-forward': PiSkipForwardBold,
}

const labelMap: Record<ButtonTypes, string> = {
  play: 'Play',
  pause: 'Pause',
  'skip-back': 'Previous',
  'skip-forward': 'Next',
}

/**
 * Player button component for media controls with different button types
 * Provides play, pause, skip-back, and skip-forward buttons with appropriate icons
 * Uses design system components with accessibility support
 * @param onClick - Callback function when button is clicked
 * @param type - Button type: 'play', 'pause', 'skip-back', or 'skip-forward'
 * @param size - IconButton size; defaults to the player's own '3'
 */
const PlayerButton: (props: Props) => React.ReactElement<'button'> = ({
  onClick,
  type = 'play',
  size = '3',
}) => {
  const theme = useContext(ThemeContext)?.theme

  const isBig = type === 'play' || type === 'pause'
  let color: 'gray' | 'yellow' | undefined = isBig ? undefined : 'gray'
  let variant: 'solid' | 'ghost' = isBig ? 'solid' : 'ghost'

  if (theme === 'dark' && isBig) {
    color = 'yellow'
    variant = 'solid'
  }

  const label = labelMap[type]
  const Icon = iconMap[type]
  return (
    <IconButton
      radius="full"
      size={size}
      variant={variant}
      color={color}
      onClick={onClick}
    >
      <AccessibleIcon label={label}>
        <Icon />
      </AccessibleIcon>
    </IconButton>
  )
}

export default PlayerButton
